import { useState, useEffect } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { WaybillForm } from "@/components/WaybillForm";
import { useStore } from "@/store";
import { fetchWaybill } from "@/api";
import { WaybillType } from "@/types";

type WaybillDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: number;
};

export function WaybillDialog({
  open,
  onOpenChange,
  invoiceId,
}: WaybillDialogProps): React.JSX.Element {
  const { waybills, setWaybills } = useStore((s) => s);
  const [waybill, setWaybill] = useState<WaybillType | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && invoiceId) {
      setLoading(true);
      // Check if waybill exists in store first
      const existingWaybill = waybills.find((w) => w.invoice_id === invoiceId);
      if (existingWaybill) {
        setWaybill(existingWaybill);
        setLoading(false);
      } else {
        // Fetch from API
        fetchWaybill(String(invoiceId))
          .then((response) => {
            const { data, error } = response;
            if (error && error.code !== "PGRST116") {
              // PGRST116 is "not found" error, which is fine
              console.error("Error fetching waybill:", error);
            }
            // Type guard: ensure data is a valid WaybillType (not an error object)
            // Supabase can return error objects in the data field, so we need to check
            if (!error && data) {
              const dataAsUnknown = data as unknown;
              // Check if it's an error object (has 'error' property)
              if (
                typeof dataAsUnknown === "object" &&
                dataAsUnknown !== null &&
                !("error" in dataAsUnknown) &&
                "id" in dataAsUnknown &&
                "invoice_id" in dataAsUnknown &&
                "driver_id" in dataAsUnknown &&
                "transport_id" in dataAsUnknown &&
                typeof (dataAsUnknown as { id: unknown }).id === "number"
              ) {
                const waybillData = dataAsUnknown as WaybillType;
                setWaybill(waybillData);
                setWaybills([...waybills, waybillData]);
              } else {
                setWaybill(null);
              }
            } else {
              setWaybill(null);
            }
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  }, [open, invoiceId, waybills, setWaybills]);

  function handleWaybillSubmit() {
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{waybill ? "Redaguoti važtaraštį" : "Sukurti važtaraštį"}</DialogTitle>
          <DialogDescription>
            {waybill
              ? "Atnaujinkite važtaraščio informaciją."
              : "Užpildykite važtaraščio informaciją šiai sąskaitai."}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center">Kraunasi...</div>
        ) : (
          <WaybillForm
            invoiceId={invoiceId}
            waybill={waybill}
            onWaybillFormSubmit={handleWaybillSubmit}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
